/**
 * How a language's words sit on a page (1.04.5, the owner, 26 October 2026: "Add hindi, Arabic, Russian and German"):
 * Arabic reads right to left, and its letters join (and Hindi's run under one headline), which the menu's spaced-out
 * capitals would tear apart.
 *
 * A page of text (About, Rules, Privacy, the live stats, the profile pages) is turned round whole (`dir="rtl"`). The
 * game's menu and its HUD keep their places (the panels, the bars, the slots and the map are drawn for one side): only
 * their words read right to left, each sentence, label and line of text in its own box.
 */

/** the languages read right to left */
export const RIGHT_TO_LEFT: ReadonlySet<string> = new Set(["ar"]);

/** the languages whose letters must not be spaced out (Arabic's join; Hindi's hang from one line) */
const UNSPACED: ReadonlySet<string> = new Set(["ar", "hi"]);

/** whether `language`'s letters may be spaced out (a canvas's words too: Pixi draws spaced letters one by one) */
export const spacedLetters = (language: string): boolean => !UNSPACED.has(language);

/**
 * a line of `language`'s words as a canvas draws it (the map's names, the Training Range's signs): Arabic's read right
 * to left as a whole (a canvas reads left to right, so "… Fargo" put its Latin word first)
 */
export const canvasLine = (text: string, language: string): string => (RIGHT_TO_LEFT.has(language) ? `‫${text}‬` : text);

/** each language's full stop where it isn't "." (sentences the game ends itself) */
export const FULL_STOPS: Readonly<Record<string, string>> = { zh: "。", hi: "।" };

/**
 * the system's fonts for Arabic's and Hindi's letters where a page asks for a monospace font (the live stats', the
 * profile pages' and About's labels): no monospace font draws them well (one that has them gives each letter a Latin
 * letter's width, Arabic's torn apart), so those letters and the spaces between their words (a monospace font's space
 * is more than twice theirs) come from these, each a name a player's system may have the font by (Android's and
 * Linux's, Windows', a Mac's), the page's own font still drawing its Latin letters and digits
 */
const SCRIPT_FONTS: Readonly<Record<string, { readonly range: string, readonly regular: readonly string[], readonly bold: readonly string[] }>> = {
    ar: {
        range: "U+0020, U+00A0, U+0600-06FF, U+0750-077F, U+08A0-08FF, U+FB50-FDFF, U+FE70-FEFF",
        regular: ["Noto Sans Arabic", "Noto Sans Arabic Regular", "NotoSansArabic-Regular", "Noto Naskh Arabic", "Noto Naskh Arabic Regular", "NotoNaskhArabic-Regular", "Segoe UI", "Tahoma", "Arial", "ArialMT", "Geeza Pro", "GeezaPro"],
        bold: ["Noto Sans Arabic Bold", "NotoSansArabic-Bold", "Noto Naskh Arabic Bold", "NotoNaskhArabic-Bold", "Segoe UI Bold", "SegoeUI-Bold", "Tahoma Bold", "Tahoma-Bold", "Arial Bold", "Arial-BoldMT", "Geeza Pro Bold", "GeezaPro-Bold"]
    },
    hi: {
        range: "U+0020, U+00A0, U+0900-097F, U+A8E0-A8FF, U+1CD0-1CFF",
        regular: ["Noto Sans Devanagari", "Noto Sans Devanagari Regular", "NotoSansDevanagari-Regular", "Nirmala UI", "NirmalaUI", "Mangal", "Kohinoor Devanagari Regular", "KohinoorDevanagari-Regular", "Devanagari Sangam MN", "DevanagariSangamMN"],
        bold: ["Noto Sans Devanagari Bold", "NotoSansDevanagari-Bold", "Nirmala UI Bold", "NirmalaUI-Bold", "Mangal Bold", "Mangal-Bold", "Kohinoor Devanagari Bold", "KohinoorDevanagari-Bold", "Devanagari Sangam MN Bold", "DevanagariSangamMN-Bold"]
    }
};

/**
 * the pages' monospace families, each with its own faces' weights: a face stands in for another's letters only at the
 * very same weight and style (one of a range of weights is a face apart, never asked), so each weight gets its own.
 * IBM Plex Mono is the stats', profile and player pages' (pages.css); Consolas, About's, is the system's own font, and a
 * family a `@font-face` names hides the system's font of that name, so its faces are named again for its Latin letters
 */
const MONOSPACE_FACES: ReadonlyArray<{ readonly family: string, readonly weights: readonly number[], readonly system?: true }> = [
    { family: "IBM Plex Mono", weights: [400, 500, 600] },
    { family: "Consolas", weights: [400, 700], system: true }
];

const fromSystem = (names: readonly string[]): string => names.map(name => `local("${name}")`).join(", ");

/** the `@font-face` rules that draw `language`'s letters where a page asks for a monospace font (none for the others) */
export const scriptFontFaces = (language: string): string[] => {
    const fonts = SCRIPT_FONTS[language];
    if (!fonts) return [];
    return MONOSPACE_FACES.flatMap(({ family, weights, system }) => weights.flatMap(weight => {
        const bold = weight >= 600;
        return [
            ...(system ? [`@font-face { font-family: "${family}"; src: ${fromSystem(bold ? [`${family} Bold`, `${family}-Bold`] : [family])}; font-weight: ${weight}; }`] : []),
            // (the last of a weight's faces is the first asked for a letter in its range)
            `@font-face { font-family: "${family}"; src: ${fromSystem(bold ? fonts.bold : fonts.regular)}; font-weight: ${weight}; unicode-range: ${fonts.range}; }`
        ];
    }));
};

/** the game's page: its words' boxes, read right to left (the boxes themselves stay where they are) */
const WORDS_RIGHT_TO_LEFT = ":is(p, li, h1, h2, h3, h4, h5, h6, dt, dd, td, th, label, legend, summary, blockquote, figcaption, [t], [translation], .rtl-words) { direction: rtl; }";

/** a page in `language`: `wholePage` for a page of text (turned round whole), else the game's menu (its words only) */
export function applyLanguageLook(language: string, wholePage: boolean): void {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const rules: string[] = [];
    if (RIGHT_TO_LEFT.has(language)) {
        if (wholePage) root.dir = "rtl";
        else rules.push(WORDS_RIGHT_TO_LEFT);
    } else if (root.dir === "rtl") {
        root.dir = "ltr";
    }
    if (UNSPACED.has(language)) rules.push("* { letter-spacing: normal !important; }", ...scriptFontFaces(language));
    document.getElementById("language-look")?.remove();
    if (!rules.length) return;
    const style = document.createElement("style");
    style.id = "language-look";
    style.textContent = rules.join("\n");
    document.head.append(style);
}
